/**
 * Rejects an account not yet verified, or revokes a verified one. Both are final: the dialog says
 * the account cannot be used again and that adding it again makes a new account.
 */
import { useState } from 'react';
import { ConfirmationModal, InputWrapper, Modal, Textarea } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { useBankAccountMutations } from '../hooks/use-bank-account-mutations';
import type { PartyBankAccount } from '../hooks/use-party-bank-accounts';
import {
  BankAccountError,
  bankAccountError,
  isStaleViewRefusal
} from '../utils/bank-account-error';
import { BankAccountSummary } from './BankAccountSummary';

export type TerminalBankAccountAction = 'reject' | 'revoke';

interface TerminalBankAccountModalProps {
  account: PartyBankAccount;
  action: TerminalBankAccountAction;
  onClose: () => void;
}

export function TerminalBankAccountModal({
  account,
  action,
  onClose
}: TerminalBankAccountModalProps) {
  const t = useFeatureTranslation('partyBankAccounts');
  const { showSuccess } = useToastHelpers();
  const { reject, revoke, refresh } = useBankAccountMutations();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<BankAccountError | null>(null);
  const [saving, setSaving] = useState(false);
  const isReject = action === 'reject';

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    const input = { id: account.id, reason: reason.trim() || null };
    try {
      if (isReject) await reject(input);
      else await revoke(input);
      showSuccess(isReject ? t('toast.rejected') : t('toast.revoked'));
      onClose();
    } catch (err) {
      const refusal = bankAccountError(err);
      // The account changed meanwhile: the list shows it as it is now.
      if (isStaleViewRefusal(refusal)) await refresh();
      setError(refusal);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConfirmationModal
      open
      testId='terminal-bank-account-modal'
      variant='danger'
      title={isReject ? t('terminal.rejectTitle') : t('terminal.revokeTitle')}
      message={isReject ? t('terminal.rejectMessage') : t('terminal.revokeMessage')}
      confirmLabel={isReject ? t('terminal.confirmReject') : t('terminal.confirmRevoke')}
      cancelLabel={t('terminal.cancel')}
      loading={saving}
      onConfirm={() => void handleConfirm()}
      onClose={onClose}
      body={
        <div className='flex flex-col gap-md'>
          <BankAccountSummary account={account} />
          <InputWrapper label={t('terminal.reason')} htmlFor='bank-account-reason' labelSize='sm'>
            <Textarea
              id='bank-account-reason'
              testId='bank-account-reason'
              value={reason}
              onChange={event => setReason(event.target.value)}
            />
          </InputWrapper>
          {error && <Modal.Error role='alert'>{t(`errors.${error}`)}</Modal.Error>}
        </div>
      }
    />
  );
}
